import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";
import _ from "lodash";

import klerpLogoPng from "../../assets/fonts/klerp-logo.png";
import {
    shouldApplySst,
    applySst,
    sstAmount,
} from "../../helpers/sst";

const PAGE_W = 595;
const INVOICE_MARGIN = 30;
const INVOICE_RIGHT = 560;
const SUMMARY_LABEL_X = 350;
const SUMMARY_VALUE_RIGHT = 555;

const NAVY = rgb(0.106, 0.165, 0.255);
const LIGHT_PANEL = rgb(0.93, 0.94, 0.96);
const PAGE_MARGIN = 30;
const CONTENT_WIDTH = 530;

const COLORS = {
    black: rgb(0, 0, 0),
    navy: NAVY,
    white: rgb(1, 1, 1),
    muted: rgb(0.38, 0.38, 0.42),
    line: rgb(0.82, 0.84, 0.88),
    tableStripe: rgb(0.965, 0.975, 0.99),
    summaryPanel: rgb(0.94, 0.96, 0.98),
};

const ITEM_NAME_MAX_WIDTH = 108;
const DESC_COL_MAX_WIDTH = 238;
const ITEM_LINE_HEIGHT = 11;
const ITEM_EXTRA_ROW_UNIT = 0.55;

const TABLE_COL = {
    NO: 40,
    ITEM: 58,
    DESCRIPTION: 172,
    QTY_RIGHT: 468,
    TOTAL_RIGHT: SUMMARY_VALUE_RIGHT,
};

const currencySymbol = () =>
    process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";

const drawRightAlignedText = (page, text, rightX, y, { font, size, color }) => {
    const width = font.widthOfTextAtSize(text, size);
    page.drawText(text, {
        x: rightX - width,
        y,
        size,
        font,
        color,
    });
};

const drawHorizontalRule = (
    page,
    y,
    { left = INVOICE_MARGIN, right = INVOICE_RIGHT, color = COLORS.line } = {}
) => {
    page.drawLine({
        start: { x: left, y },
        end: { x: right, y },
        thickness: 0.75,
        color,
    });
};

const sortInvoiceProducts = (products) => {
    const typeOrder = {
        JINKO: 1,
        BIFICIAL: 2,
        INVERTER: 3,
        WARRANTY: 4,
        ADDON: 5,
        OTHERS: 6,
        EQUIPMENT: 7,
    };
    products.sort(
        (a, b) => b.price * b.amount - a.price * a.amount
    );
    products.sort((a, b) => {
        const typeA = typeOrder[a.type] || 999;
        const typeB = typeOrder[b.type] || 999;
        return typeA - typeB;
    });
};

const getCustomerAddressLines = (original) => {
    const addr = original.userId?.address || {};
    const lineOne = addr.lineOne || original.lineOne || "";
    const lineTwo = addr.lineTwo || original.lineTwo || "";
    const zip = addr.zip || original.zip || "";
    const city = addr.city || original.city || "";
    const country = addr.country || original.country || "";
    return {
        line1: [lineOne, lineTwo].filter(Boolean).join(", ") || "-",
        line2: [zip, city, country].filter(Boolean).join(", ") || "-",
    };
};

const splitItemAndDescription = (code) => {
    const raw = String(code ?? "").trim();
    if (!raw) {
        return { item: "-", description: "-" };
    }
    const separatorIndex = raw.indexOf(" - ");
    if (separatorIndex === -1) {
        return { item: raw, description: "-" };
    }
    return {
        item: raw.slice(0, separatorIndex).trim() || "-",
        description: raw.slice(separatorIndex + 3).trim() || "-",
    };
};

const wrapTextToWidth = (text, font, fontSize, maxWidth) => {
    const raw = String(text ?? "-").trim() || "-";
    const words = raw.split(/\s+/);
    const lines = [];
    let current = "";

    const pushLine = (line) => {
        if (line) lines.push(line);
    };

    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
            current = candidate;
            continue;
        }
        pushLine(current);
        if (font.widthOfTextAtSize(word, fontSize) <= maxWidth) {
            current = word;
            continue;
        }
        let chunk = "";
        for (const ch of word) {
            const test = chunk + ch;
            if (font.widthOfTextAtSize(test, fontSize) <= maxWidth) {
                chunk = test;
            } else {
                pushLine(chunk);
                chunk = ch;
            }
        }
        current = chunk;
    }
    pushLine(current);
    return lines.length ? lines : ["-"];
};

const drawInvoiceProductRows = (
    page,
    pageData,
    pageIndex,
    {
        height,
        fontSize,
        standardFontSize,
        timesRomanFont,
        NumberFormatter,
    }
) => {
    let tableRowUnits = 0;

    pageData.forEach((product, index) => {
        const { item, description } = splitItemAndDescription(product.code);
        const itemLines = wrapTextToWidth(
            item,
            timesRomanFont,
            standardFontSize,
            ITEM_NAME_MAX_WIDTH
        ).slice(0, 3);
        const descLines = wrapTextToWidth(
            description,
            timesRomanFont,
            standardFontSize,
            DESC_COL_MAX_WIDTH
        ).slice(0, 4);
        const lineCount = Math.max(itemLines.length, descLines.length, 1);
        const rowY = height - (14.9 + tableRowUnits) * fontSize;
        const bandHeight = Math.max(14, lineCount * ITEM_LINE_HEIGHT + 8);

        if (index % 2 === 1) {
            page.drawRectangle({
                x: INVOICE_MARGIN,
                y: rowY - bandHeight + 10,
                width: INVOICE_RIGHT - INVOICE_MARGIN,
                height: bandHeight,
                color: COLORS.tableStripe,
                borderWidth: 0,
            });
        }

        page.drawText(`${index + 1 + pageIndex * 15}`, {
            x: TABLE_COL.NO,
            y: rowY,
            size: standardFontSize,
            font: timesRomanFont,
            color: COLORS.black,
        });

        itemLines.forEach((line, lineIndex) => {
            page.drawText(line, {
                x: TABLE_COL.ITEM,
                y: rowY - lineIndex * ITEM_LINE_HEIGHT,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        });

        descLines.forEach((line, lineIndex) => {
            page.drawText(line, {
                x: TABLE_COL.DESCRIPTION,
                y: rowY - lineIndex * ITEM_LINE_HEIGHT,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        });

        drawRightAlignedText(
            page,
            `${product.quantity || "-"}`,
            TABLE_COL.QTY_RIGHT,
            rowY,
            {
                font: timesRomanFont,
                size: standardFontSize,
                color: COLORS.black,
            }
        );

        const lineTotal =
            product.price !== 0 && product.price !== undefined
                ? NumberFormatter.format(product.price * product.quantity)
                : "-";
        drawRightAlignedText(page, lineTotal, TABLE_COL.TOTAL_RIGHT, rowY, {
            font: timesRomanFont,
            size: standardFontSize,
            color: COLORS.black,
        });

        tableRowUnits += 1 + (lineCount - 1) * ITEM_EXTRA_ROW_UNIT;
    });

    return tableRowUnits;
};

const drawAppendixSectionHeading = (page, title, y, fonts) => {
    const { bold, size } = fonts;
    const barHeight = 24;

    page.drawRectangle({
        x: PAGE_MARGIN,
        y: y - barHeight + 8,
        width: CONTENT_WIDTH,
        height: barHeight,
        color: NAVY,
    });

    page.drawText(title, {
        x: PAGE_MARGIN + 14,
        y: y - 2,
        size: size + 1,
        font: bold,
        color: COLORS.white,
    });

    return y - barHeight - 14;
};

const drawPaymentTermsAcceptance = (page, startY, fonts) => {
    const { bold, regular, size } = fonts;
    const title = "PAYMENT TERMS & ACCEPTANCE";
    let y = startY;

    page.drawText(title, {
        x: PAGE_MARGIN,
        y,
        size: size + 1,
        font: bold,
        color: NAVY,
    });
    page.drawLine({
        start: { x: PAGE_MARGIN, y: y - 5 },
        end: {
            x: PAGE_MARGIN + bold.widthOfTextAtSize(title, size + 1) + 18,
            y: y - 5,
        },
        thickness: 1.5,
        color: NAVY,
    });
    y -= size + 16;

    drawHorizontalRule(page, y);

    const terms = [
        ["50% DOWN PAYMENT", "Payable to commence the project."],
        [
            "50% BALANCE",
            "Payable upon final approval and handover, within 30 days of completion.",
        ],
    ];

    const badgeWidth = 128;
    const textX = PAGE_MARGIN + badgeWidth + 14;
    const textMaxWidth = CONTENT_WIDTH - badgeWidth - 14;
    const boxHeight = 30;

    terms.forEach(([badge, text]) => {
        y -= 6;
        page.drawRectangle({
            x: PAGE_MARGIN,
            y: y - boxHeight,
            width: badgeWidth,
            height: boxHeight,
            color: NAVY,
        });

        const badgeLines = wrapTextToWidth(badge, bold, size - 1, badgeWidth - 12);
        badgeLines.slice(0, 2).forEach((line, i) => {
            const lineWidth = bold.widthOfTextAtSize(line, size - 1);
            page.drawText(line, {
                x: PAGE_MARGIN + (badgeWidth - lineWidth) / 2,
                y: y - 14 - i * (size + 1),
                size: size - 1,
                font: bold,
                color: COLORS.white,
            });
        });

        const detailLines = wrapTextToWidth(text, regular, size, textMaxWidth);
        detailLines.forEach((line, i) => {
            page.drawText(line, {
                x: textX,
                y: y - 12 - i * (size + 2),
                size,
                font: regular,
                color: COLORS.black,
            });
        });

        y -= boxHeight + 8;
    });

    y -= 4;
    drawHorizontalRule(page, y);

    return y - 14;
};

const APPENDIX_TERMS = [
    {
        title: "Client materials",
        body: "All relevant content, references and supporting materials are to be provided by the client prior to production.",
    },
    {
        title: "Hosting arrangement",
        body: "Any external hosting requirement is to be arranged and provided by the client.",
    },
    {
        title: "Production timeline",
        body: "Estimated project duration is approximately 9 weeks, subject to timely feedback, approvals and receipt of required materials.",
    },
    {
        title: "Revision entitlement",
        body: "Up to one round of structural revisions, limited to approximately 50% of the structure differences (major change). Further revisions will be accepted with additional fees after the agreed revision deadline.",
    },
    {
        title: "Additional amendments",
        body: "Any changes or amendments beyond the agreed scope and revision entitlement will be subject to additional charges.",
    },
    {
        title: "Payment schedule",
        body: "50% down payment is payable to commence the project. The remaining 50% final balance is payable upon final approval and handover, within 30 days of completion.",
    },
];

const drawAppendixLastPage = (page, startY, fonts) => {
    const { bold, regular, size } = fonts;
    let y = drawAppendixSectionHeading(page, "Terms & Conditions", startY, fonts);

    const leftWidth = 298;
    const rightWidth = CONTENT_WIDTH - leftWidth - 18;
    const rightX = PAGE_MARGIN + leftWidth + 18;
    const contentTop = y;
    const lineGap = size + 3;

    let leftY = contentTop;
    APPENDIX_TERMS.forEach((item, index) => {
        const numLabel = `${index + 1}.`;
        const numWidth = bold.widthOfTextAtSize(numLabel, size);
        page.drawText(numLabel, {
            x: PAGE_MARGIN + 6,
            y: leftY,
            size,
            font: bold,
            color: NAVY,
        });

        const paragraph = `${item.title}: ${item.body}`;
        const lines = wrapTextToWidth(
            paragraph,
            regular,
            size,
            leftWidth - numWidth - 12
        );
        lines.forEach((line, lineIndex) => {
            page.drawText(line, {
                x: PAGE_MARGIN + 6 + numWidth + 4,
                y: leftY - lineIndex * lineGap,
                size,
                font: regular,
                color: COLORS.black,
            });
        });

        leftY -= lines.length * lineGap + 11;
    });

    const signaturePanelHeight = 128;
    const panelBottom = contentTop - signaturePanelHeight;

    page.drawRectangle({
        x: rightX,
        y: panelBottom,
        width: rightWidth,
        height: signaturePanelHeight,
        color: COLORS.summaryPanel,
        borderColor: COLORS.line,
        borderWidth: 0.75,
    });
    page.drawRectangle({
        x: rightX,
        y: panelBottom,
        width: 4,
        height: signaturePanelHeight,
        color: NAVY,
        borderWidth: 0,
    });

    const acceptance =
        "I / We hereby confirm and accept the above costs, terms & conditions.";
    let acceptY = contentTop - 14;
    wrapTextToWidth(acceptance, regular, size, rightWidth - 24).forEach(
        (line, i) => {
            page.drawText(line, {
                x: rightX + 14,
                y: acceptY - i * lineGap,
                size,
                font: regular,
                color: COLORS.black,
            });
        }
    );

    const signLineY = panelBottom + 36;
    page.drawLine({
        start: { x: rightX + 14, y: signLineY },
        end: { x: rightX + rightWidth - 14, y: signLineY },
        thickness: 0.85,
        color: COLORS.black,
    });
    page.drawText("Client's Signature and Company's Stamp", {
        x: rightX + 14,
        y: signLineY - 14,
        size: size - 1,
        font: regular,
        color: COLORS.muted,
    });

    y = drawPaymentTermsAcceptance(
        page,
        Math.min(leftY, panelBottom) - 22,
        fonts
    );

    const paymentMode =
        "Payment Mode : CHEQUE Payable to Klerp Tech Sdn Bhd or ONLINE TRANSFER (Maybank Acc: 5148 9713 3467).";
    const modeLines = wrapTextToWidth(
        paymentMode,
        bold,
        size,
        CONTENT_WIDTH - 24
    );
    const modeBoxHeight = modeLines.length * lineGap + 16;

    page.drawRectangle({
        x: PAGE_MARGIN,
        y: y - modeBoxHeight,
        width: CONTENT_WIDTH,
        height: modeBoxHeight,
        color: COLORS.summaryPanel,
        borderColor: NAVY,
        borderWidth: 0.75,
    });

    modeLines.forEach((line, i) => {
        page.drawText(line, {
            x: PAGE_MARGIN + 12,
            y: y - 12 - i * lineGap,
            size,
            font: bold,
            color: NAVY,
        });
    });

    return y - modeBoxHeight - 8;
};

const drawInvoicePageHeader = (
    page,
    { height, fontSize, standardFontSize, fonts, logoImage, logoDims }
) => {
    const { regular, bold } = fonts;

    page.drawRectangle({
        x: 0,
        y: height - 5,
        width: PAGE_W,
        height: 5,
        color: NAVY,
    });

    const logoY = height - 1.2 * fontSize - logoDims.height;
    page.drawImage(logoImage, {
        x: 10,
        y: logoY,
        width: logoDims.width,
        height: logoDims.height,
    });

    const companyBlockX = 10 + logoDims.width + 10;
    page.drawText(`KLERP TECH SDN BHD (202301037428 (1531351-D))`, {
        x: companyBlockX,
        y: height - 1.9 * fontSize,
        size: standardFontSize,
        font: bold,
        color: COLORS.black,
    });
    page.drawText(
        `C3-3-13A, Solaris Dutamas, Jln Solaris Dutamas, Taman Sri Hartamas`,
        {
            x: companyBlockX,
            y: height - 2.7 * fontSize,
            size: standardFontSize,
            font: regular,
            color: COLORS.muted,
        }
    );
    page.drawText(`50450 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur`, {
        x: companyBlockX,
        y: height - 3.4 * fontSize,
        size: standardFontSize,
        font: regular,
        color: COLORS.muted,
    });

    const contactRows = [
        ["T", "+6 012 5488 100"],
        ["E", "admin@klerptech.com"],
        ["W", "www.klerptech.com"],
    ];
    contactRows.forEach(([label, value], idx) => {
        const rowY = height - (4.1 + idx * 0.7) * fontSize;
        const valueWidth = regular.widthOfTextAtSize(value, standardFontSize);
        const labelWidth = bold.widthOfTextAtSize(label, standardFontSize);
        page.drawText(value, {
            x: INVOICE_RIGHT - valueWidth,
            y: rowY,
            size: standardFontSize,
            font: regular,
            color: COLORS.black,
        });
        page.drawText(label, {
            x: INVOICE_RIGHT - valueWidth - labelWidth - 4,
            y: rowY,
            size: standardFontSize,
            font: bold,
            color: COLORS.navy,
        });
    });
};

const drawBillToSection = (
    page,
    original,
    { height, fontSize, standardFontSize, fonts, referenceLabel = "INVOICE ID:" }
) => {
    const { regular, bold } = fonts;
    const { line1, line2 } = getCustomerAddressLines(original);
    const fields = [
        [referenceLabel, `${_.padStart(original.invoiceId, 4, "0")}`],
        ["DATE OF ISSUE:", moment(original.createdAt).format("DD MMM YYYY")],
        ["ACCOUNTS NAME:", original.userId.name],
        ["PHONE NUMBER:", original.userId.phoneNumber],
        ["EMAIL:", original.userId.email || "-"],
        ["ADDRESS:", line1],
    ];

    page.drawRectangle({
        x: INVOICE_MARGIN - 4,
        y: height - 11.85 * fontSize,
        width: INVOICE_RIGHT - INVOICE_MARGIN + 8,
        height: 4.85 * fontSize,
        color: LIGHT_PANEL,
        borderColor: COLORS.line,
        borderWidth: 0.5,
    });

    fields.forEach(([label, value], index) => {
        const rowY = height - (7.5 + index * 0.7) * fontSize;
        page.drawText(label, {
            x: INVOICE_MARGIN,
            y: rowY,
            size: standardFontSize,
            font: bold,
            color: COLORS.muted,
        });
        page.drawText(String(value), {
            x: 140,
            y: rowY,
            size: standardFontSize,
            font: regular,
            color: COLORS.black,
        });
    });

    page.drawText(line2, {
        x: 140,
        y: height - 11.7 * fontSize,
        size: standardFontSize,
        font: regular,
        color: COLORS.black,
    });
};

const drawLineItemsTableHeader = (page, { height, fontSize, standardFontSize, fonts }) => {
    const { bold } = fonts;
    const headerY = height - 13 * fontSize;
    const barHeight = 18;

    page.drawRectangle({
        x: INVOICE_MARGIN,
        y: headerY - 6,
        width: INVOICE_RIGHT - INVOICE_MARGIN,
        height: barHeight,
        color: NAVY,
    });

    const headers = [
        { text: "NO", x: TABLE_COL.NO },
        { text: "ITEM", x: TABLE_COL.ITEM },
        { text: "DESCRIPTION", x: TABLE_COL.DESCRIPTION },
        { text: "QTY", x: TABLE_COL.QTY_RIGHT - bold.widthOfTextAtSize("QTY", standardFontSize) },
        { text: "TOTAL (RM)", x: TABLE_COL.TOTAL_RIGHT - bold.widthOfTextAtSize("TOTAL (RM)", standardFontSize) },
    ];

    headers.forEach(({ text, x }) => {
        page.drawText(text, {
            x,
            y: headerY,
            size: standardFontSize,
            font: bold,
            color: COLORS.white,
        });
    });

    drawHorizontalRule(page, height - 13.35 * fontSize, { color: NAVY });
};

const drawSummaryAmountLine = (
    page,
    y,
    label,
    amountText,
    fonts,
    { emphasize = false } = {}
) => {
    const { regular, bold } = fonts;
    page.drawText(label, {
        x: SUMMARY_LABEL_X,
        y,
        size: emphasize ? 10 : 9,
        font: emphasize ? bold : regular,
        color: COLORS.black,
    });
    drawRightAlignedText(page, amountText, SUMMARY_VALUE_RIGHT, y, {
        font: emphasize ? bold : regular,
        size: emphasize ? 10 : 9,
        color: emphasize ? COLORS.navy : COLORS.black,
    });
};

const drawFinancialSummary = (
    page,
    {
        height,
        fontSize,
        tableRowUnits,
        totalAmount,
        discount,
        creditVal,
        original,
        applySstTax,
        deposit,
        invoicePaidVal,
        NumberFormatter,
        fonts,
        showPaymentBreakdown = true,
    }
) => {
    const sym = currencySymbol();
    const baseTotal = totalAmount - discount - creditVal;

    drawHorizontalRule(page, height - (15.5 + tableRowUnits) * fontSize);

    const yAt = (row) => height - (row + tableRowUnits) * fontSize;

    drawSummaryAmountLine(
        page,
        yAt(16.5),
        "SUB TOTAL:",
        `${sym} ${NumberFormatter.format(totalAmount)}`,
        fonts
    );

    if (discount !== 0) {
        drawSummaryAmountLine(
            page,
            yAt(17.2),
            "DISCOUNT:",
            `${sym} ${NumberFormatter.format(discount)}`,
            fonts
        );
    }

    if (original.credit) {
        drawSummaryAmountLine(
            page,
            yAt(18.2),
            "TRADE IN:",
            `${sym} ${NumberFormatter.format(original.credit.credit)}`,
            fonts
        );
    }

    let summaryRow = 19.2;
    if (applySstTax) {
        drawSummaryAmountLine(
            page,
            yAt(summaryRow),
            "SST (8%):",
            `${sym} ${NumberFormatter.format(
                sstAmount(
                    baseTotal,
                    original.commenceDate,
                    original.sstIncluded
                )
            )}`,
            fonts
        );
        summaryRow += 1;
    }

    const finalTotal = applySst(
        baseTotal,
        original.commenceDate,
        original.sstIncluded
    );
    const totalY = yAt(summaryRow);

    page.drawRectangle({
        x: SUMMARY_LABEL_X - 8,
        y: totalY - 6,
        width: INVOICE_RIGHT - SUMMARY_LABEL_X + 8,
        height: 20,
        color: COLORS.summaryPanel,
        borderColor: COLORS.line,
        borderWidth: 0.5,
    });

    drawSummaryAmountLine(
        page,
        totalY,
        "TOTAL:",
        `${sym} ${NumberFormatter.format(finalTotal)}`,
        fonts,
        { emphasize: true }
    );

    const balance = finalTotal - deposit;
    const showPaymentsAndBalance =
        showPaymentBreakdown && invoicePaidVal !== 0 && balance !== 0;
    const paymentStartRow = summaryRow + 1;

    if (showPaymentsAndBalance) {
        const payments = [
            ["1ST PAYMENT:", invoicePaidVal],
            ["2ND PAYMENT:", original.secondPayment],
            ["3RD PAYMENT:", original.thirdPayment],
            ["BALANCE:", balance],
        ];
        payments.forEach(([label, val], i) => {
            const isOptionalPayment =
                label === "2ND PAYMENT:" || label === "3RD PAYMENT:";
            const amount = isOptionalPayment
                ? val
                    ? `${sym} ${NumberFormatter.format(val)}`
                    : "-"
                : `${sym} ${NumberFormatter.format(Number(val) || 0)}`;
            drawSummaryAmountLine(
                page,
                yAt(paymentStartRow + i),
                label,
                amount,
                fonts
            );
        });
    }

    const summaryExtra = showPaymentsAndBalance ? 4 : 0;
    const lineRow = paymentStartRow + summaryExtra;
    drawHorizontalRule(page, height - (lineRow + tableRowUnits) * fontSize);

    return { lineRow, tableRowUnits };
};

const drawNotesSection = (page, { height, fontSize, lineRow, tableRowUnits, fonts }) => {
    const { regular, bold } = fonts;
    const notesY = (offset) =>
        height - (lineRow + offset + tableRowUnits) * fontSize;

    page.drawText("NOTES", {
        x: INVOICE_MARGIN,
        y: notesY(1.5),
        size: 9,
        font: bold,
        color: COLORS.navy,
    });
    page.drawText(`1. All payments shall be made payable to Klerp Tech Sdn Bhd.`, {
        x: INVOICE_MARGIN,
        y: notesY(2.5),
        size: 9,
        font: regular,
        color: COLORS.black,
    });
    page.drawText(
        `2. Payments must be settled via direct deposit or bank transfer to Maybank 5148 9713 3467.`,
        {
            x: INVOICE_MARGIN,
            y: notesY(3.3),
            size: 9,
            font: regular,
            color: COLORS.black,
        }
    );
};

const drawKlerpFooterBar = (page, fonts) => {
    const { regular, size } = fonts;
    const barHeight = 22;
    page.drawRectangle({
        x: 0,
        y: 0,
        width: 595,
        height: barHeight,
        color: NAVY,
    });
    const footerText =
        "KLERP TECH SDN BHD (202301037428 (1531351-D)) | +6 012 5488 100 | admin@klerptech.com | www.klerptech.com";
    const textWidth = regular.widthOfTextAtSize(footerText, size - 1);
    page.drawText(footerText, {
        x: Math.max(PAGE_MARGIN, (595 - textWidth) / 2),
        y: 6,
        size: size - 1,
        font: regular,
        color: rgb(1, 1, 1),
    });
};

export const drawOrderAppendixPage = (pdfDoc, fonts) => {
    const termsPage = pdfDoc.addPage();
    const { height } = termsPage.getSize();

    termsPage.drawRectangle({
        x: 0,
        y: height - 5,
        width: PAGE_W,
        height: 5,
        color: NAVY,
    });

    drawAppendixLastPage(termsPage, height - 40, fonts);
    drawKlerpFooterBar(termsPage, fonts);
};

export const generateKlerpOrderPdf = async (
    original,
    {
        referenceLabel = "INVOICE ID:",
        showPaymentBreakdown = true,
        downloadFileName,
        includeAppendix = true,
    }
) => {
    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const standardFontSize = 9;

    const totalPages =
        original.products.length <= 15
            ? 1
            : Math.ceil((original.products.length - 15) / 15 + 1);

    const pdfDoc = await PDFDocument.create();

    const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
    const timesRomanBoldFont = await pdfDoc.embedFont(
        StandardFonts.TimesRomanBold
    );

    const amount = Number(original.amount) || 0;
    const discount = Number(original.discount) || 0;
    const deposit = Number(original.deposit) || Number(original.invoicePaid) || 0;
    const invoicePaidVal =
        Number(original.invoicePaid) || Number(original.deposit) || 0;
    const creditVal = original.credit ? Number(original.credit.credit) || 0 : 0;
    const applySstTax = shouldApplySst(
        original.commenceDate,
        original.sstIncluded
    );

    sortInvoiceProducts(original.products);

    const logoImageBytes = await fetch(klerpLogoPng).then((res) =>
        res.arrayBuffer()
    );
    const logoImage = await pdfDoc.embedPng(new Uint8Array(logoImageBytes));
    const logoDims = logoImage.scale(0.13);

    const fonts = {
        regular: timesRomanFont,
        bold: timesRomanBoldFont,
    };

    for (let i = 0; i < totalPages; i++) {
        const page = pdfDoc.addPage();
        const fontSize = 20;
        const { height } = page.getSize();
        const pageData = original.products.slice(i * 15, (i + 1) * 15);
        const isLastPage = i === totalPages - 1;

        drawInvoicePageHeader(page, {
            height,
            fontSize,
            standardFontSize,
            fonts,
            logoImage,
            logoDims,
        });

        drawBillToSection(page, original, {
            height,
            fontSize,
            standardFontSize,
            fonts,
            referenceLabel,
        });

        drawHorizontalRule(page, height - 12.4 * fontSize);
        drawLineItemsTableHeader(page, {
            height,
            fontSize,
            standardFontSize,
            fonts,
        });

        const tableRowUnits = drawInvoiceProductRows(page, pageData, i, {
            height,
            fontSize,
            standardFontSize,
            timesRomanFont,
            NumberFormatter,
        });

        if (isLastPage) {
            const { lineRow, tableRowUnits: rowUnits } = drawFinancialSummary(
                page,
                {
                    height,
                    fontSize,
                    tableRowUnits,
                    totalAmount: amount,
                    discount,
                    creditVal,
                    original,
                    applySstTax,
                    deposit,
                    invoicePaidVal,
                    NumberFormatter,
                    fonts,
                    showPaymentBreakdown,
                }
            );
            drawNotesSection(page, {
                height,
                fontSize,
                lineRow,
                tableRowUnits: rowUnits,
                fonts,
            });
        }
    }

    if (includeAppendix) {
        drawOrderAppendixPage(pdfDoc, {
            bold: timesRomanBoldFont,
            regular: timesRomanFont,
            size: standardFontSize,
        });
    }

    const pdfBytes = await pdfDoc.save();
    download(pdfBytes, downloadFileName, "application/pdf");
};
